/**
 * Helper matching filename ↔ ragione sociale (Phase 9.5 anti-errore upload).
 *
 * Algoritmo:
 * 1. Normalizza filename: lowercase, rimuovi estensione, sostituisci
 *    `_`/`-`/`.`/spazi multipli con singolo spazio, trim.
 * 2. Normalizza ragione sociale: lowercase, rimuovi suffissi societari IT
 *    (srl/spa/sas/snc/società/scarl/impresa individuale), sostituisci
 *    punteggiatura con spazi, trim.
 * 3. Estrai token distintivi: parole ≥3 caratteri, escludendo stopword IT
 *    (di, del, della, e, il, la, lo, i, gli, le, per).
 * 4. Match: almeno 60% dei token della company presenti come substring
 *    nel filename normalizzato. Se company ha ≤2 token, richiedi 100%.
 */

// Suffissi societari italiani da rimuovere prima del tokenizing.
// Sia forme puntate (S.R.L.) sia compatte (SRL) sia forme parola (società, impresa individuale).
const IT_COMPANY_SUFFIXES = /\b(s\.?r\.?l\.?|s\.?p\.?a\.?|s\.?a\.?s\.?|s\.?n\.?c\.?|societ[àa]|s\.?c\.?a\.?r\.?l\.?|impresa\s+individuale)\b/gi;

const IT_STOPWORDS = new Set([
    "di", "del", "della", "e", "il", "la", "lo", "i", "gli", "le", "per",
]);

// Rimuove accenti/diacritici per matching più robusto (società → societa).
function stripAccents(s) {
    return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function normalizeFilename(name) {
    if (!name) return "";
    // Rimuovi estensione
    const withoutExt = name.replace(/\.[^.]+$/, "");
    return stripAccents(withoutExt)
        .toLowerCase()
        .replace(/[_\-./,;:]+/g, " ")
        .replace(/[^a-z0-9\s]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export function normalizeCompanyName(name) {
    if (!name) return "";
    return stripAccents(name)
        .toLowerCase()
        .replace(IT_COMPANY_SUFFIXES, " ")
        .replace(/[^a-z0-9\s]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export function extractDistinctiveTokens(normalizedText) {
    if (!normalizedText) return [];
    return normalizedText
        .split(/\s+/)
        .filter((t) => t.length >= 3 && !IT_STOPWORDS.has(t));
}

/**
 * Ritorna true se il filename normalizzato contiene abbastanza token
 * distintivi della company normalizzata (soglia 60% oppure 100% se
 * la company ha ≤2 token).
 */
export function fileMatchesCompany(filename, companyName) {
    const fn = normalizeFilename(filename);
    const cn = normalizeCompanyName(companyName);
    if (!fn || !cn) return false;
    const companyTokens = extractDistinctiveTokens(cn);
    if (companyTokens.length === 0) return false;
    const threshold = companyTokens.length <= 2 ? 1.0 : 0.6;
    const matched = companyTokens.filter((t) => fn.includes(t)).length;
    return matched / companyTokens.length >= threshold;
}

/**
 * Trova il PRIMO cliente diverso da `selectedClientId` che matcha il filename.
 * Ritorna null se nessun cliente diverso matcha (upload coerente o generico).
 *
 * `clients` = [{id, company_name}, ...]
 */
export function findMismatchedClient(filename, selectedClientId, clients) {
    for (const c of clients || []) {
        if (!c || !c.company_name) continue;
        if (c.id === selectedClientId) continue;
        if (fileMatchesCompany(filename, c.company_name)) {
            return c;
        }
    }
    return null;
}
